import { useEffect, useState } from "react";
import { Info, Loader2 } from "lucide-react";
import { api } from "../../lib/api";
import { Badge, EmptyRow, LEVEL_LABELS, PanelTitle, StatCard, Table, fmtDate, shortHash } from "./ui";
import { RankBadge, RanksCard } from "./RanksCard";

const fmt = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD`;
const periodLabel = (p) => new Date(`${p}-01T12:00:00Z`).toLocaleDateString("es", { month: "long", year: "numeric" });
export const KIND_LABELS = { personal: "Ventas personales", red: "Ganancias en red", rango: "Estímulo por rango" };
export const kindDetail = (h) => {
  if (h.kind === "personal") return `${h.detail.units} códigos`;
  if (h.kind === "rango") return `${h.detail.rank} · ${Math.round(h.detail.pct * 100)}% de ${fmt(h.detail.global_sales)} entre ${h.detail.qualifiers}`;
  return Object.entries(h.detail.units_by_level || {}).map(([l, u]) => `N${l}: ${u}`).join(" · ");
};

export const MonthlyBreakdown = ({ line, tiers, perLevel }) => (
  <div className="grid gap-4 md:grid-cols-2" data-testid="monthly-breakdown">
    <div className="rounded-2xl border border-ink/10 bg-white p-5">
      <p className="font-mono text-xs uppercase tracking-[0.14em] text-leaf-deep">Ventas personales (1er nivel + clientes)</p>
      <p className="mt-2 font-display text-3xl font-semibold">{fmt(line?.personal_amount)}</p>
      <p className="text-sm text-ink/70">{line?.personal_units || 0} códigos vendidos este periodo</p>
      <ul className="mt-3 space-y-1 text-xs text-ink/70">
        {(line?.personal_tiers || []).map((t, i) => <li key={i} className="flex justify-between"><span>{t.units} código(s) × {fmt(t.rate)}</span><span className="font-semibold text-ink">{fmt(t.amount)}</span></li>)}
        {!line?.personal_units && <li>Escala: {tiers.map((t) => `${t.up_to ? `hasta ${t.up_to}` : "61+"} → $${t.rate}`).join(" · ")}</li>}
      </ul>
    </div>
    <div className="rounded-2xl border border-ink/10 bg-white p-5">
      <p className="font-mono text-xs uppercase tracking-[0.14em] text-leaf-deep">Ganancias en red (${perLevel} por código, 5 niveles)</p>
      <p className="mt-2 font-display text-3xl font-semibold">{fmt(line?.red_amount)}</p>
      <ul className="mt-3 grid grid-cols-5 gap-1 text-center text-xs">
        {Object.entries(line?.red_units || { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }).map(([l, u]) => (
          <li key={l} className="rounded-xl bg-paper py-2"><p className="font-semibold">{u}</p><p className="text-[10px] text-ink/60">{LEVEL_LABELS[l]}</p></li>
        ))}
      </ul>
    </div>
  </div>
);

export const MonthlyPanel = () => {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/me/monthly").then(({ data }) => setD(data));
  }, []);
  if (!d) return <Loader2 className="h-6 w-6 animate-spin" />;
  const proj = d.projection;
  return (
    <div data-testid="monthly-panel">
      <PanelTitle title="Ganancias mensuales" text={`Por cada código de acceso ($${d.unit_price} USD) que compra tu red o que vendes a clientes: ganancia escalonada por ventas de tu 1er nivel, $${d.network_per_level} USD por código en 5 niveles y estímulos por rango (ORO, DIAMANTE, CORONA). Se liquidan con el corte mensual del administrador.`} />
      <div className="mt-6 grid grid-cols-3 gap-3">
        <StatCard dark label={`Proyección ${periodLabel(d.period)}`} value={fmt(proj?.total)} testId="monthly-projection" />
        <StatCard label="Cobrado (cortes definitivos)" value={fmt(d.paid)} testId="monthly-paid" />
        <StatCard label="Pendiente de pago" value={fmt(d.pending)} testId="monthly-pending" />
      </div>
      <p className="mt-4 flex items-start gap-2 rounded-xl border border-sun/60 bg-sun/15 px-4 py-3 text-sm" data-testid="monthly-notice">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-[#8a6a00]" />
        <span>{d.definitive_cut ? "Este mes ya tiene corte definitivo." : <>Las cifras de este mes son una <strong>proyección</strong>{d.partial_cut_at ? ` (último corte parcial: ${fmtDate(d.partial_cut_at)})` : ""}. <strong>El corte definitivo y verdadero se aplica al final de mes.</strong> Solo cuentan compras confirmadas por el administrador.</>}</span>
      </p>
      <div className="mt-6"><MonthlyBreakdown line={proj} tiers={d.tiers} perLevel={d.network_per_level} /></div>
      <div className="mt-4"><RanksCard ranks={d.ranks} myRank={d.my_rank} globalSales={d.global_sales} bonus={proj?.rank_bonus} /></div>

      <h3 className="mt-10 font-display text-xl font-semibold">Historial de cortes</h3>
      <Table headers={["Periodo", "Concepto", "Detalle", "Monto", "Estado", "Pagada el", "Hash"]} testId="monthly-history" minWidth="min-w-[820px]">
        {!d.history.length && <EmptyRow cols={7} text="Aún no hay cortes definitivos con ganancias para ti." testId="monthly-history-empty" />}
        {d.history.map((h, i) => (
          <tr key={h.id} className="border-b border-ink/5 last:border-0" data-testid={`monthly-row-${i}`}>
            <td className="px-4 py-3 capitalize">{periodLabel(h.period)}</td>
            <td className="px-4 py-3 font-semibold">{KIND_LABELS[h.kind]} {h.kind === "rango" && <RankBadge rank={h.detail.rank} />}</td>
            <td className="px-4 py-3 text-xs text-ink/70">{kindDetail(h)}</td>
            <td className="px-4 py-3 font-display text-base font-semibold">{fmt(h.amount)}</td>
            <td className="px-4 py-3"><Badge status={h.status} label={h.status === "pagada" ? "Cobrada" : "Pendiente"} /></td>
            <td className="px-4 py-3 text-ink/70">{fmtDate(h.paid_at)}</td>
            <td className="px-4 py-3 font-mono text-xs">{shortHash(h.paid_tx_hash)}</td>
          </tr>
        ))}
      </Table>
    </div>
  );
};
